"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, BookOpen, Sparkles, ShieldCheck, ArrowRight } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Intro() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    initial: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  };
  const principles = [{
    id: "p1",
    title: "Positive reinforcement",
    description: "Reward-based protocols backed by modern veterinary behavioral science."
  }, {
    id: "p2",
    title: "Feline enrichment",
    description: "Environmental optimization, vertical territory, and stress reduction for indoor cats."
  }, {
    id: "p3",
    title: "Clinical psychology",
    description: "Separation anxiety, fear reactivity, and cognitive wellness assessments."
  }];
  return <section id="intro" data-nav="light" className="relative bg-background text-foreground py-12 md:py-16 lg:py-20 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Editorial Eyebrow & Category Label */}
        <motion.div initial={fadeIn.initial} animate={fadeIn.animate} transition={fadeIn.transition} className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-muted text-foreground/80 border border-border">
            <Sparkles className="size-3.5 text-chart-1" />
            Evidence-Informed Psychology
          </span>
          <span className="text-xs text-muted-foreground font-medium">
            Clinical Behavioral Standards
          </span>
        </motion.div>

        {/* Main Grid: Editorial Content & Subject Image */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: Heading & Core Text */}
          <div className="lg:col-span-7 flex flex-col justify-center">
            <motion.h1 initial={fadeIn.initial} animate={fadeIn.animate} transition={{
            ...fadeIn.transition,
            delay: 0.05
          }} className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.15]">
              Behavior & Training
            </motion.h1>

            <motion.p initial={fadeIn.initial} animate={fadeIn.animate} transition={{
            ...fadeIn.transition,
            delay: 0.1
          }} className="mt-5 text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
              Understand what drives your pet. We replace outdated punishment myths with humane,
              science-backed learning theory, practical training plans, and feline behavioral
              insights for a calmer home.
            </motion.p>

            {/* Clinical Governance Callout Box */}
            <motion.div initial={fadeIn.initial} animate={fadeIn.animate} transition={{
            ...fadeIn.transition,
            delay: 0.15
          }} className="mt-6 rounded-r-xl border border-border border-l-4 border-l-chart-1 bg-card p-5 sm:p-6 shadow-xs">
              <div className="flex items-start gap-3">
                <ShieldCheck className="size-5 text-chart-1 shrink-0 mt-0.5" />
                <div>
                  <h2 className="text-sm font-semibold text-foreground tracking-tight">
                    Veterinary Behavioral Standard
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground leading-normal">
                    All guides align with force-free, positive reinforcement methodologies endorsed
                    by board-certified veterinary behaviorists and animal welfare researchers.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Core Focus Pillars */}
            <motion.div initial={fadeIn.initial} animate={fadeIn.animate} transition={{
            ...fadeIn.transition,
            delay: 0.2
          }} className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {principles.map((item, i) => <div key={item.id} data-index={i} className="p-4 rounded-lg bg-card/60 border border-border/80 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-1.5 text-foreground font-semibold text-sm">
                      <CheckCircle2 className="size-4 text-chart-1" />
                      <span>{item.title}</span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>)}
            </motion.div>

            {/* Primary Actions */}
            <motion.div initial={fadeIn.initial} animate={fadeIn.animate} transition={{
            ...fadeIn.transition,
            delay: 0.25
          }} className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-lg font-medium shadow-sm">
                <Link to="/DogsPage" className="inline-flex items-center gap-2">
                  <span>Dog training guides</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-lg font-medium border-border hover:bg-muted text-foreground">
                <Link to="/CatsPage" className="inline-flex items-center gap-2">
                  <BookOpen className="size-4 text-muted-foreground" />
                  <span>Cat behavior library</span>
                </Link>
              </Button>
            </motion.div>
          </div>

          {/* Right Column: Documentary Photography Card */}
          <motion.div initial={fadeIn.initial} animate={fadeIn.animate} transition={{
          ...fadeIn.transition,
          delay: 0.2
        }} className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-md">
              <div className="relative aspect-[4/5] sm:aspect-[4/3] lg:aspect-[4/5] w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/owner_training_dog_with_treats.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/owner_training_dog_with_treats.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/owner_training_dog_with_treats.png-wvc-sizes" alt="Pet owner calmly training a dog using positive reinforcement and treats" loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-500 hover:scale-102" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                
                {/* Image Overlay Caption */}
                <div className="absolute bottom-0 inset-x-0 p-5 text-white">
                  <span className="inline-block px-2.5 py-0.5 rounded text-[11px] font-semibold bg-chart-1 text-primary-foreground mb-1.5 uppercase tracking-wider">
                    In Practice
                  </span>
                  <p className="text-sm font-medium text-white/95 leading-snug">
                    Calm, marker-based positive reinforcement builds reliable communication without intimidation.
                  </p>
                </div>
              </div>

              {/* Card Meta Footer */}
              <div className="p-4 bg-card border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span className="font-medium text-foreground/80">Behavioral Foundation Series</span>
                <span className="flex items-center gap-1">
                  <span className="inline-block size-1.5 rounded-full bg-chart-1" />
                  Reviewed by Certified Behaviorists
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}